import { Modal } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import { PrinterOutlined, FileTextOutlined } from "@ant-design/icons";
import { useState } from "react";
import {
  printPersonalReport,
  printPersonalReportPreview,
  type PaymentBreakdownItem,
  type PreviewPersonalReportRequest,
  type Report,
} from "../../../../../api/reports";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import { useViewport } from "../../../../../hooks/useViewport";
import { usePrinterNotifications } from "../../../../../hooks/usePrinterNotifications";
import { getApiErrorMessage } from "../../../../../utils/translations";
import {
  ModalHeader,
  GhostButton,
  PrimaryButton,
  footerButtonHeight,
} from "../../modalPrimitives";
import { useAppSelector } from "../../../../../store/hooks";
import {
  printReportDocument,
  reportPrintCss,
} from "./reportDocumentPrint";
import { ReportDocumentHeading } from "./ReportDocumentHeading";
import { SoldSellablesSection } from "./SoldSellablesSection";
import { ReportTipsSection } from "./ReportTipsSection";
import { ReportAmountToReport } from "./ReportAmountToReport";
import { ReportDepositsSection } from "./ReportDepositsSection";
import { blockIfLicenseReadOnly } from "../../../../../lib/licenseReadOnly";
import { reportFooterColumns } from "./reportFooterColumns";

interface PersonalReportPreviewModalProps {
  open: boolean;
  onClose: () => void;
  report: Report | null;
  isPreviewMode?: boolean;
  onConfirmCreate?: () => void;
  isCreating?: boolean;
  /** Тялото, с което е направен прегледът — „Печат ПОС" преди „Нулирай" го
   *  праща отново, за да излезе същият билет (отчет с id още няма). */
  previewRequest?: PreviewPersonalReportRequest | null;
  /** „Междинен отчет": само прегледът — без футъра (без печат и без
   *  издаване), затваря се от X горе вдясно, а документът взима и височината
   *  на футъра. */
  hideFooter?: boolean;
}

export const PersonalReportPreviewModal = ({
  open,
  onClose,
  report,
  isPreviewMode = false,
  onConfirmCreate,
  isCreating = false,
  previewRequest = null,
  hideFooter = false,
}: PersonalReportPreviewModalProps) => {
  const { t, i18n } = useTranslation();
  const { isMobile, isNarrow, isTablet } = useViewport();
  const message = useOrderingMessage();
  const { notifyPrinterError } = usePrinterNotifications();
  const [isPrintingPos, setIsPrintingPos] = useState(false);
  const restaurantName = useAppSelector(
    (s) => s.auth.user?.restaurant_display_name ?? "",
  );
  if (!report) return null;

  const handlePrint = () => {
    if (blockIfLicenseReadOnly()) return;
    printReportDocument();
  };

  // Print on the local POS (ESC/POS) printer assigned to the current user.
  // BE returns 400 when no POS printer is configured for the operator —
  // we surface that as a readable toast instead of a generic "failed".
  // Записаният отчет се печата по id, прегледът — по тялото си.
  const canPrintPos = isPreviewMode
    ? previewRequest !== null
    : typeof report.id === "number";
  const handlePrintPos = async () => {
    if (!canPrintPos || isPrintingPos) return;
    if (blockIfLicenseReadOnly()) return;
    setIsPrintingPos(true);
    try {
      if (isPreviewMode && previewRequest) {
        await printPersonalReportPreview(previewRequest);
      } else {
        await printPersonalReport(report.id as number);
      }
      message.success(t("personalReport.printPosSuccess"));
    } catch (error: unknown) {
      notifyPrinterError(
        getApiErrorMessage(error, t("personalReport.printPosError")),
      );
    } finally {
      setIsPrintingPos(false);
    }
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format("DD.MM.YYYY");
  };

  const formatTime = (dateString: string | null) => {
    if (!dateString) return "-";
    return dayjs(dateString).format("HH:mm:ss");
  };

  const formatCurrency = (amount: number | string | null | undefined) => {
    if (amount === null || amount === undefined) return "0.00";
    return Number(amount).toFixed(2);
  };

  // bills_count от payment_breakdown е Decimal-string и може да е дробен
  // (mixed се разделя пропорционално). Цели стойности — без десетични.
  const formatBillsCount = (count: number | string) => {
    const n = Number(count);
    if (!Number.isFinite(n)) return String(count);
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
  };

  // Едно място за заглавието — показва се и в хедъра на модала, и вътре в
  // документа (само при печат), за да не се разминават.
  const documentTitle = t("personalReport.title", {
    number: report.report_number,
    date: formatDate(report.report_date),
  });
  const documentSubtitle = `${restaurantName} · ${report.user_name}`;

  // Взетите депозити са в своята секция отдолу (`ReportDepositsSection`) —
  // таблицата по методи е само оборотът по сметки.
  const paymentRows: PaymentBreakdownItem[] | null =
    report.payment_breakdown && report.payment_breakdown.length > 0
      ? report.payment_breakdown
      : null;
  const isEn = (i18n.language ?? "bg").toLowerCase().startsWith("en");
  const pmName = (item: PaymentBreakdownItem) =>
    isEn ? item.name_en : item.name_bg;

  const width = isMobile ? undefined : isTablet ? 640 : 720;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
      zIndex={isPreviewMode ? 2500 : 1000}
    >
      <style>{reportPrintCss}</style>
      <div className="bg-pos-card font-pos-sans text-pos-ink flex flex-col max-h-[90vh]">
        <ModalHeader
          icon={<FileTextOutlined />}
          title={documentTitle}
          subtitle={documentSubtitle}
          onClose={onClose}
          isNarrow={isNarrow}
        />

      <div className="report-doc-paper bg-surface text-ink p-6 overflow-y-auto min-h-0">
        <ReportDocumentHeading
          title={documentTitle}
          subtitle={documentSubtitle}
        />

        <div className="mb-6">
          <h3 className="text-base font-bold text-gray-800 bg-gray-200 px-2 py-1 mb-2">
            {t("personalReport.turnoverByPayment")}
          </h3>
          <table className="w-full border-collapse border border-gray-400 text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-400 px-2 py-1 text-left">
                  {t("personalReport.paymentType")}
                </th>
                <th className="border border-gray-400 px-2 py-1 text-center">
                  {t("personalReport.bills")}
                </th>
                <th className="border border-gray-400 px-2 py-1 text-center">
                  {t("common.total")}
                </th>
              </tr>
            </thead>
            <tbody>
              {paymentRows ? (
                paymentRows.map((row) => (
                  <tr
                    key={row.payment_method_id}
                    className={!row.is_active ? "italic text-gray-500" : ""}
                  >
                    <td className="border border-gray-400 px-2 py-1">
                      {pmName(row)}
                    </td>
                    <td className="border border-gray-400 px-2 py-1 text-center">
                      {formatBillsCount(row.bills_count)}
                    </td>
                    <td className="border border-gray-400 px-2 py-1 text-right font-semibold">
                      {formatCurrency(row.amount)}
                    </td>
                  </tr>
                ))
              ) : (
                <>
                  <tr>
                    <td className="border border-gray-400 px-2 py-1">
                      {t("personalReport.cashPayment")}
                    </td>
                    <td className="border border-gray-400 px-2 py-1 text-center">
                      {report.cash_bills_count}
                    </td>
                    <td className="border border-gray-400 px-2 py-1 text-right font-semibold">
                      {formatCurrency(report.cash_amount)}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-400 px-2 py-1">
                      {t("personalReport.cardPayment")}
                    </td>
                    <td className="border border-gray-400 px-2 py-1 text-center">
                      {report.card_bills_count}
                    </td>
                    <td className="border border-gray-400 px-2 py-1 text-right font-semibold">
                      {formatCurrency(report.card_amount)}
                    </td>
                  </tr>
                </>
              )}
              <tr className="bg-gray-100 font-bold">
                <td className="border border-gray-400 px-2 py-1" colSpan={2}>
                  {t("common.total")}
                </td>
                <td className="border border-gray-400 px-2 py-1 text-right">
                  {formatCurrency(report.total_amount)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Бакшишът — само когато го има (иначе не рендира нищо). */}
        <ReportTipsSection report={report} />

        {/* Взетите депозити — само когато ги има; „Сума за отчитане" отдолу ги включва. */}
        <ReportDepositsSection report={report} />

        <SoldSellablesSection
          sellables={report.sold_sellables_summary ?? []}
        />

        {report.cancelled_items_summary &&
          report.cancelled_items_summary.length > 0 && (
            <div className="mb-6">
              <h3 className="text-base font-bold text-gray-800 bg-gray-200 px-2 py-1 mb-2">
                {t("personalReport.cancelledByStation")}
              </h3>
              <table className="w-full border-collapse border border-gray-400 text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-400 px-2 py-1 text-left">
                      {t("personalReport.bar")}
                    </th>
                    <th className="border border-gray-400 px-2 py-1 text-center">
                      {t("personalReport.qty")}
                    </th>
                    <th className="border border-gray-400 px-2 py-1 text-right">
                      {t("common.amount")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {report.cancelled_items_summary.map((item, index) => (
                    <tr key={index}>
                      <td className="border border-gray-400 px-2 py-1">
                        {item.item_name}
                      </td>
                      <td className="border border-gray-400 px-2 py-1 text-center">
                        {item.quantity}
                      </td>
                      <td className="border border-gray-400 px-2 py-1 text-right">
                        {formatCurrency(Number(item.total_amount))}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-100 font-bold">
                    <td
                      className="border border-gray-400 px-2 py-1"
                      colSpan={2}
                    >
                      {t("common.total")}
                    </td>
                    <td className="border border-gray-400 px-2 py-1 text-right">
                      {formatCurrency(
                        report.cancelled_items_summary.reduce(
                          (sum, item) => sum + (Number(item.total_amount) || 0),
                          0,
                        ),
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

        <div className="grid grid-cols-2 gap-4 text-sm mb-6">
          <div>
            <p className="font-semibold">
              {t("personalReport.firstLastOrder")}
            </p>
            <p>
              {formatTime(report.first_order_time)} /{" "}
              {formatTime(report.last_order_time)}
            </p>
          </div>
          <div>
            <p className="font-semibold">
              {t("personalReport.cashDescription")}
            </p>
            <p className="text-gray-500">x 100 = ...................</p>
            <p className="text-gray-500">x 50 = ...................</p>
            <p className="text-gray-500">x 20 = ...................</p>
            <p className="text-gray-500">x 10 = ...................</p>
            <p className="text-gray-500">x 5 = ...................</p>
            <p className="text-gray-500">x 2 = ...................</p>
            <p className="text-gray-500">x 1 = ...................</p>
            <p className="text-gray-500">
              {t("personalReport.coins")} ...................
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm mb-6">
          <div>
            <p className="font-semibold">{t("personalReport.firstLastBill")}</p>
            <p>
              {formatTime(report.first_bill_closed)} /{" "}
              {formatTime(report.last_bill_closed)}
            </p>
          </div>
          <div className="border-t-2 border-gray-800 pt-2">
            {/* `amount_to_report` от BE (без бакшиш = в брой, както досега);
                отрицателното добавя ред „Доплащане към персонала". */}
            <ReportAmountToReport report={report} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 text-sm border-t-2 border-gray-800 pt-4">
          <div>
            <p className="mb-8">
              {t("personalReport.accepted")} ...................................
            </p>
            <p className="text-xs text-gray-500 italic">
              {dayjs(report.created_at).format("DD-MM-YYYY HH:mm:ss")} /{" "}
              {/* Отчетите отпреди снимката на името нямат `created_by_name` —
                  там остава голото id, за да не изчезне следата изцяло. */}
              {report.created_by_name ?? report.created_by_user_id}
            </p>
          </div>
          <div>
            <p>
              {t("personalReport.created")} ...................................
            </p>
            <p className="mt-2">
              {t("personalReport.dateLabel")}{" "}
              ...................................
            </p>
          </div>
        </div>

      </div>

      {!hideFooter && (
      <div
        className={`grid border-t border-pos-line2 ${
          isNarrow ? "gap-2.5 px-4 py-3" : "gap-4 px-6 py-3.5"
        } ${reportFooterColumns(isPreviewMode, isNarrow)}`}
      >
          {isPreviewMode ? (
            <>
              <div className="flex">
                <GhostButton
                  height={footerButtonHeight(isNarrow)}
                  label={t("common.cancel")}
                  onClick={onClose}
                  disabled={isCreating}
                />
              </div>
              <div className="flex">
                <GhostButton
                  height={footerButtonHeight(isNarrow)}
                  label={t("common.print")}
                  onClick={handlePrint}
                />
              </div>
              <div className="flex">
                <GhostButton
                  height={footerButtonHeight(isNarrow)}
                  label={t("generateReport.printPosButton")}
                  onClick={handlePrintPos}
                  loading={isPrintingPos}
                  disabled={!canPrintPos || isCreating}
                />
              </div>
              <div className="flex">
                <PrimaryButton
                  height={footerButtonHeight(isNarrow)}
                  label={t("generateReport.resetButton")}
                  onClick={() => onConfirmCreate?.()}
                  loading={isCreating}
                />
              </div>
            </>
          ) : (
            <>
              <div className="flex">
                <GhostButton
                  height={footerButtonHeight(isNarrow)}
                  label={t("common.close")}
                  onClick={onClose}
                />
              </div>
              <div className="flex">
                <GhostButton
                  height={footerButtonHeight(isNarrow)}
                  label={t("common.print")}
                  onClick={handlePrint}
                />
              </div>
              <div className="flex">
                <PrimaryButton
                  height={footerButtonHeight(isNarrow)}
                  label={t("personalReport.printPos")}
                  onClick={handlePrintPos}
                  icon={<PrinterOutlined />}
                  loading={isPrintingPos}
                  disabled={!canPrintPos}
                />
              </div>
            </>
          )}
      </div>
      )}
      </div>
    </Modal>
  );
};
